import { Building2, ExternalLink, MessageSquare } from "lucide-react";
import { Badge } from "@foundation/components/ui/badge";
import type { AiSkill, BoostPolicyBrandingData, BoostPolicyFormData, OrganizationProfileData } from "../../types/boostPolicy";
import { AiSkillsSelector } from "./AiSkillsSelector";
// import { TemplatePreviewCard } from "./TemplatePreviewCard"; // reserved for future template selection

const INDUSTRY_LABELS: Record<string, string> = {
  manufacturing: 'Manufacturing',
  construction: 'Construction',
  healthcare: 'Healthcare',
  oil_gas: 'Oil & Gas',
  mining: 'Mining',
  transport_logistics: 'Transportation & Logistics',
  food_beverage: 'Food & Beverage',
  utilities: 'Utilities',
  government: 'Government',
  professional_services: 'Professional Services',
  other: 'Other',
};

const SIZE_LABELS: Record<string, string> = {
  '1-10': '1–10 employees',
  '11-50': '11–50 employees',
  '51-200': '51–200 employees',
  '201-500': '201–500 employees',
  '500+': '500+ employees',
};

const DEFAULT_BRANDING: BoostPolicyBrandingData = {
  primary_color: '#000000',
  secondary_color: '#000000',
  font_color: '#000000',
  font_family: 'Inter',
};

const MATURITY_LABELS: Record<string, string> = {
  low:    'Low',
  medium: 'Medium',
  high:   'High',
};

type QASessionPair = { question: string; answer: string };

type GeneratePolicyStepProps = {
  data: BoostPolicyFormData;
  brandingData: BoostPolicyBrandingData | null;
  orgProfile?: OrganizationProfileData | null;
  skills: AiSkill[];
  isSkillsLoading: boolean;
  onSkillsChange: (selectedIds: string[]) => void;
  qaMemoryContent?: string;
  qaSessionPairs?: QASessionPair[];
};

export const GeneratePolicyStep = ({
  data,
  brandingData,
  orgProfile,
  skills,
  isSkillsLoading,
  onSkillsChange,
  qaSessionPairs,
}: GeneratePolicyStepProps) => {
  const hasProfile =
    orgProfile?.industry || orgProfile?.org_size || orgProfile?.maturityLevel || orgProfile?.jurisdiction || (orgProfile?.standards?.length ?? 0) > 0;
  return (
    <div className="space-y-5">
      {/* Top row: Policy Name + Selected Document */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Policy Name */}
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
            Policy Name
          </p>
          <p className="text-lg font-semibold text-foreground">{data.policyName}</p>
        </div>

        {/* Selected Standard Document */}
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
            Source Document
          </p>
          <p className="text-base font-semibold text-foreground">
            {data.selectedKnowledgeDocument?.title || "Not selected"}
          </p>
          {data.selectedKnowledgeDocument?.description && (
            <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
              {data.selectedKnowledgeDocument.description}
            </p>
          )}
          {data.selectedKnowledgeDocument?.tags && data.selectedKnowledgeDocument.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {data.selectedKnowledgeDocument.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Template — hidden for now, will be re-enabled in future
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Selected Template
        </p>
        {data.selectedTemplateData ? (
          <div className="space-y-2">
            <TemplatePreviewCard template={data.selectedTemplateData} height={220} />
            <p className="text-sm font-semibold text-foreground text-center">
              {data.selectedTemplateData.name}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">No template — generate only</p>
        )}
      </div>
      */}

      {/* Organisation Profile */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Organisation Profile
          </p>
          <a
            href="/settings?tab=branding&section=profile"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
          >
            Edit in Settings
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {hasProfile ? (
          <div className="rounded-lg border border-border bg-background divide-y divide-border">
            {/* Industry + Size + Maturity */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 py-4">
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Industry</p>
                {orgProfile?.industry ? (
                  <Badge variant="secondary" className="text-sm font-normal">
                    {INDUSTRY_LABELS[orgProfile.industry] ?? orgProfile.industry}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Organisation Size</p>
                {orgProfile?.org_size ? (
                  <Badge variant="secondary" className="text-sm font-normal">
                    {SIZE_LABELS[orgProfile.org_size] ?? orgProfile.org_size}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Governance Maturity</p>
                {orgProfile?.maturityLevel ? (
                  <Badge variant="secondary" className="text-sm font-normal">
                    {MATURITY_LABELS[orgProfile.maturityLevel] ?? orgProfile.maturityLevel}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
            </div>

            {orgProfile?.jurisdiction && (
              <div className="px-4 py-4 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Primary Jurisdiction</p>
                <p className="text-sm text-foreground">{orgProfile.jurisdiction}</p>
              </div>
            )}

            {orgProfile?.standards && orgProfile.standards.length > 0 && (
              <div className="px-4 py-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Standards & Certifications</p>
                <div className="flex flex-wrap gap-2">
                  {orgProfile.standards.map((s) => (
                    <Badge key={s} variant="outline" className="text-xs border-primary/40 text-primary bg-primary/5">
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground flex items-start gap-2">
            <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              No organisation profile set.{' '}
              <a href="/settings?tab=branding&section=profile" className="text-primary hover:underline inline-flex items-center gap-0.5">
                Set it up in Settings → Branding → Org Profile
                <ExternalLink className="h-3 w-3" />
              </a>
            </span>
          </div>
        )}
      </div>

      {/* Organisation Context (free text) */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Organisation Context
        </p>
        {data.organizationContext ? (
          <p className="text-sm text-foreground leading-relaxed line-clamp-4">{data.organizationContext}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">Not provided</p>
        )}
      </div>

      {/* AI Context — step 2 relevant context + step 3 session Q&A */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5" />
          AI Context
        </p>

        {/* Step 2: relevant context items */}
        {(data.relevantContext ?? []).length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Relevant context (from Step 2)</p>
            <div className="space-y-1.5">
              {(data.relevantContext ?? []).map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
                  <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[#33b864] flex-shrink-0" />
                  <p className="text-sm text-foreground leading-snug">{item}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Q&A pairs from this session */}
        {qaSessionPairs && qaSessionPairs.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Context Q&amp;A (from Step 3)</p>
            <div className="space-y-2">
              {qaSessionPairs.map((pair, idx) => (
                <div key={idx} className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{pair.question}</p>
                  <p className="text-sm text-foreground">{pair.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: additional free-text context */}
        {data.additionalContext?.trim() && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Additional context (from Step 3)</p>
            <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
              <p className="text-sm text-foreground leading-relaxed">{data.additionalContext}</p>
            </div>
          </div>
        )}

        {(data.relevantContext ?? []).length === 0 && (!qaSessionPairs || qaSessionPairs.length === 0) && !data.additionalContext?.trim() && (
          <p className="text-sm text-muted-foreground italic">No context collected yet.</p>
        )}
      </div>

      {/* AI Skills */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          AI Skills
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          Select skills to shape how the AI generates your policy. Skills inject specialised instructions into the generation process.
        </p>
        <AiSkillsSelector
          skills={skills}
          selectedIds={data.selectedSkillIds}
          isLoading={isSkillsLoading}
          onChange={onSkillsChange}
        />
      </div>

      {/* Branding */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Branding
        </p>
        {(() => {
          // Always show branding values, falling back to defaults per-field so
          // the colour swatches are never blank when branding is partial/absent.
          const source = brandingData ?? DEFAULT_BRANDING;
          const b = {
            ...source,
            primary_color: source.primary_color || DEFAULT_BRANDING.primary_color,
            secondary_color: source.secondary_color || DEFAULT_BRANDING.secondary_color,
            font_color: source.font_color || DEFAULT_BRANDING.font_color,
            font_family: source.font_family || DEFAULT_BRANDING.font_family,
          };
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {/* Logo */}
              <div className="flex flex-col items-center gap-2">
                {b.logo_url ? (
                  <img
                    src={b.logo_url as string}
                    alt="Logo"
                    className="h-16 w-16 rounded-lg object-contain border border-border bg-muted p-1"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-lg border border-dashed border-border bg-muted flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">No logo</span>
                  </div>
                )}
                <span className="text-xs text-muted-foreground">Logo</span>
              </div>

              {/* Colours */}
              <div className="flex flex-col gap-3">
                <p className="text-xs font-medium text-muted-foreground">Colours</p>
                <div className="flex items-center gap-3">
                  {b.primary_color && (
                    <div className="flex items-center gap-2">
                      <div
                        className="h-8 w-8 rounded-md border border-border shadow-sm"
                        style={{ backgroundColor: b.primary_color as string }}
                      />
                      <div>
                        <span className="text-xs text-muted-foreground block">Primary</span>
                        <span className="text-xs font-mono text-foreground">{b.primary_color as string}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {b.secondary_color && (
                    <div className="flex items-center gap-2">
                      <div
                        className="h-8 w-8 rounded-md border border-border shadow-sm"
                        style={{ backgroundColor: b.secondary_color as string }}
                      />
                      <div>
                        <span className="text-xs text-muted-foreground block">Secondary</span>
                        <span className="text-xs font-mono text-foreground">{b.secondary_color as string}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Font */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-muted-foreground">Font Family</p>
                {b.font_family ? (
                  <p
                    className="text-base font-medium text-foreground"
                    style={{ fontFamily: b.font_family as string }}
                  >
                    {b.font_family as string}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Default</p>
                )}
              </div>

              {/* Tagline */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-muted-foreground">Tagline</p>
                {b.tagline ? (
                  <p className="text-sm text-foreground italic">
                    &ldquo;{b.tagline as string}&rdquo;
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Not set</p>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
